// src/utils/gleichstand.js
//
// Anzeige der Gleichstandsinformationen, die das Backend zentral berechnet
// (functions/services/rangliste.js). Hier wird NICHTS gerechnet – nur das
// Feld `gleichstand` eines Ranking-Eintrags in lesbare Hinweise uebersetzt.
//
// Regel (zur Erinnerung): Punkte, dann mehr 1. Plaetze an Stationen, dann mehr
// 2. Plaetze usw. Ist alles gleich, teilen sich die Teams den Platz.

/**
 * Hinweise zu einem Team. `nurTeamTyp` beschraenkt auf punktgleiche Teams
 * desselben Team-Typs (fuer Preise mit rankType "Teamtyp").
 *
 * `anonym` laesst die Namen der anderen Teams weg – fuer die Siegerehrung auf
 * dem Beamer. Sonst stuende dort z. B. "Punktgleich mit Team X – dahinter",
 * und das Publikum wuesste den Platz von Team X, bevor es aufgerufen wird.
 *
 * Rueckgabe: { offen, hinweise: [{ art: "offen" | "entschieden", text }] }
 * oder null, wenn das Team mit niemandem punktgleich ist.
 */
export function gleichstandsHinweise(team, { nurTeamTyp = false, anonym = false } = {}) {
  const g = team?.gleichstand;
  if (!g || !Array.isArray(g.vergleiche)) return null;

  const vergleiche = nurTeamTyp
    ? g.vergleiche.filter((v) => (v.team_type ?? null) === (team.team_type ?? null))
    : g.vergleiche;
  if (vergleiche.length === 0) return null;

  const alle = vergleiche.map((v) => {
    const name = anonym ? "einem anderen Team" : v.team_name || "unbekanntes Team";
    if (v.ergebnis === "gleich") {
      return {
        art: "offen",
        text: `Punktgleich mit ${name} – gleiche Stationsplatzierungen, Platz wird geteilt.`,
      };
    }
    const richtung = v.ergebnis === "vor" ? "vorne" : "dahinter";
    return {
      art: "entschieden",
      text: `Punktgleich mit ${name} – ${richtung} durch ${v.stufe}. Plätze an Stationen (${v.eigene} zu ${v.andere}).`,
    };
  });
  // Ohne Namen koennen mehrere Vergleiche denselben Text ergeben – einmal reicht.
  const hinweise = alle.filter((h, i) => alle.findIndex((x) => x.text === h.text) === i);

  return { offen: hinweise.some((h) => h.art === "offen"), hinweise };
}

/**
 * Alle Gleichstaende einer Kategorie fuer eine Uebersicht (z. B. vor dem
 * Urkundendruck). Teams ohne Punkte werden ausgelassen – dass alle Teams ohne
 * Ergebnis punktgleich sind, ist keine Warnung wert.
 */
export function gleichstaendeDerKategorie(teams, eventType) {
  return (teams || [])
    .filter((t) => t.event_type === eventType && (t.total_points || 0) > 0 && t.gleichstand)
    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity))
    .map((t) => ({ team: t, ...gleichstandsHinweise(t) }));
}

/** Hat das Ranking schon zentral vergebene Plaetze? (Alte Dokumente nicht.) */
export function rankingHatPlaetze(teams) {
  const mitPunkten = (teams || []).filter((t) => (t.total_points || 0) > 0);
  return mitPunkten.length === 0 || mitPunkten.every((t) => Number.isInteger(t.rank));
}

/** Sortierung nach zentralem Platz, bei geteiltem Platz nach Name. */
export function nachRang(a, b) {
  const ra = a.rank ?? Infinity;
  const rb = b.rank ?? Infinity;
  if (ra !== rb) return ra - rb;
  if ((b.total_points || 0) !== (a.total_points || 0)) return (b.total_points || 0) - (a.total_points || 0);
  return String(a.team_name || "").localeCompare(String(b.team_name || ""));
}
